import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import Layout from "../Layout";
import { AdminLoadingStyles } from "./AdminLoadingUi";
import { adminDeleteJson, adminGetJson } from "./adminApi";
import AdminTalkToDataTab from "./AdminTalkToDataTab";
import AdminLiveBidIntelligenceTab from "./AdminLiveBidIntelligenceTab";
import AdminContentIdeasTab from "./AdminContentIdeasTab";
import AdminInvestigatorTab from "./AdminInvestigatorTab";
import AdminSessionSidebar from "./AdminSessionSidebar";
import { EntityDrawer, TAB_META, TabButton, buttonStyle, cardStyle, muted, useSchemaOptions } from "./AdminIntelligenceShared";

const pageStyle = { maxWidth: 1600, margin: "0 auto", padding: "8px 12px", color: "#1f2937", height: "calc(100vh - 140px)", display: "flex", flexDirection: "column", overflow: "hidden", boxSizing: "border-box" };
const mobileBreak = 960;

export default function AdminIntelligencePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [schema, setSchema] = useState(null);
  const [, setSchemaLoading] = useState(true);
  const [schemaError, setSchemaError] = useState("");
  const [sessions, setSessions] = useState([]);
  const [sessionsLoading, setSessionsLoading] = useState(false);
  const [sessionCursor, setSessionCursor] = useState(null);
  const [hasMoreSessions, setHasMoreSessions] = useState(false);
  const [sessionBundle, setSessionBundle] = useState(null);
  const [sessionLoading, setSessionLoading] = useState(false);
  const [globalError, setGlobalError] = useState("");
  const [profile, setProfile] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [profileError, setProfileError] = useState("");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(typeof window !== "undefined" ? window.innerWidth < mobileBreak : false);
  const [resetNonce, setResetNonce] = useState(0);
  const sessionsRequestRef = useRef(0);
  const sessionDetailRequestRef = useRef(0);
  const currentTabRef = useRef("tab1");
  const blankTabRef = useRef("");
  // When onSessionStarted changes the URL, suppress the useEffect([currentSessionId])
  // from firing a competing loadSessionDetail that races ask()'s GET request.
  const skipNextSessionLoadRef = useRef(false);

  const currentTab = searchParams.get("tab") || "tab1";
  const currentSessionId = searchParams.get("session") || "";
  const schemaOptions = useSchemaOptions(schema);

  useEffect(() => {
    currentTabRef.current = currentTab;
  }, [currentTab]);

  useEffect(() => {
    function onResize() {
      const mobile = window.innerWidth < mobileBreak;
      setIsMobile(mobile);
      if (!mobile) setSidebarOpen(false);
    }
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setSchemaLoading(true);
        setSchemaError("");
        const res = await adminGetJson("/api/admin/intelligence/schema-context");
        if (mounted) setSchema(res.schema_context);
      } catch (err) {
        if (mounted) setSchemaError(err.message || String(err));
      } finally {
        if (mounted) setSchemaLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  async function loadSessions(tab, cursor = null, append = false) {
    const requestId = ++sessionsRequestRef.current;
    try {
      setSessionsLoading(true);
      const qs = new URLSearchParams({ tab, limit: "20" });
      if (cursor) qs.set("cursor_updated_at", cursor);
      const res = await adminGetJson(`/api/admin/intelligence/sessions?${qs.toString()}`);
      if (requestId !== sessionsRequestRef.current) return [];
      const normalizedRows = (res.rows || []).map(row => {
        if (row.status === 'running' && !row.job_state?.kind) return { ...row, status: 'complete' };
        return row;
      });
      setSessions((prev) => append ? [...prev, ...normalizedRows] : normalizedRows);
      setSessionCursor(res.next_cursor || null);
      setHasMoreSessions(Boolean(res.has_more));
      return res.rows || [];
    } catch (err) {
      setGlobalError(err.message || String(err));
      return [];
    } finally {
      setSessionsLoading(false);
    }
  }

  async function loadSessionDetail(sessionId) {
    if (!sessionId) {
      setSessionBundle(null);
      return null;
    }
    const requestId = ++sessionDetailRequestRef.current;
    try {
      setSessionLoading(true);
      const res = await adminGetJson(`/api/admin/intelligence/sessions/${encodeURIComponent(sessionId)}`);
      if (requestId !== sessionDetailRequestRef.current) return null;
      setSessionBundle({ session: res.session, messages: res.messages || [] });
      return res;
    } catch (err) {
      setGlobalError(err.message || String(err));
      setSessionBundle(null);
      return null;
    } finally {
      setSessionLoading(false);
    }
  }

  function setRunningSessionStub(tab, sessionId) {
    const nowIso = new Date().toISOString();
    setSessionBundle((prev) => ({
      session: {
        id: sessionId,
        uid: prev?.session?.uid || "",
        tab,
        title: prev?.session?.id === sessionId ? prev.session.title : "",
        status: "running",
        created_at: prev?.session?.id === sessionId ? prev.session.created_at : nowIso,
        updated_at: nowIso,
      },
      messages: prev?.session?.id === sessionId ? (prev.messages || []) : [],
    }));
  }

  useEffect(() => {
    let mounted = true;
    (async () => {
      const rows = await loadSessions(currentTab, null, false);
      if (!mounted) return;
      if (!currentSessionId) {
        setSessionBundle(null);
      }
    })();
    return () => { mounted = false; };
  }, [currentTab]);

  useEffect(() => {
    if (skipNextSessionLoadRef.current) {
      skipNextSessionLoadRef.current = false;
      return;
    }
    loadSessionDetail(currentSessionId);
  }, [currentSessionId]);

  function updateUrl(tab, sessionId = "") {
    const next = new URLSearchParams();
    next.set("tab", tab);
    if (sessionId) next.set("session", sessionId);
    setSearchParams(next, { replace: false });
  }

  function handleTabChange(tab) {
    currentTabRef.current = tab;
    blankTabRef.current = "";
    sessionsRequestRef.current += 1;
    sessionDetailRequestRef.current += 1;
    setSessions([]);
    setSessionCursor(null);
    setHasMoreSessions(false);
    setSessionBundle(null);
    setResetNonce((value) => value + 1);
    updateUrl(tab, "");
  }

  function handleNew() {
    currentTabRef.current = currentTab;
    blankTabRef.current = currentTab;
    sessionDetailRequestRef.current += 1;
    setSessionBundle(null);
    setGlobalError("");
    setResetNonce((value) => value + 1);
    updateUrl(currentTab, "");
    setSidebarOpen(false);
  }

  async function handleDelete(sessionId) {
    try {
      await adminDeleteJson(`/api/admin/intelligence/sessions/${encodeURIComponent(sessionId)}`);
      const remaining = sessions.filter((row) => row.id !== sessionId);
      setSessions(remaining);
      if (currentSessionId === sessionId) {
        updateUrl(currentTab, remaining[0]?.id || "");
      }
    } catch (err) {
      setGlobalError(err.message || String(err));
    }
  }

  function handleSessionSelect(sessionId) {
    currentTabRef.current = currentTab;
    sessionDetailRequestRef.current += 1;
    if (sessionId === currentSessionId) {
      loadSessionDetail(sessionId);
      setSidebarOpen(false);
      return;
    }
    updateUrl(currentTab, sessionId);
    setSidebarOpen(false);
  }

  const onSessionStarted = useCallback((tab, sessionId) => {
    if (currentTabRef.current !== tab) return;
    setRunningSessionStub(tab, sessionId);
    skipNextSessionLoadRef.current = true;
    updateUrl(tab, sessionId);
    // Add a running stub to the sessions list immediately without reloading.
    // The full reload happens in onSessionResolved once the job completes.
    setSessions((prev) => {
      const existing = prev.find((s) => s.id === sessionId);
      if (existing) {
        return [{ ...existing, status: "running", updated_at: new Date().toISOString() }, ...prev.filter((s) => s.id !== sessionId)];
      }
      const nowIso = new Date().toISOString();
      return [{ id: sessionId, tab, title: "Untitled", status: "running", created_at: nowIso, updated_at: nowIso }, ...prev];
    });
  }, []);

  const onSessionResolved = useCallback(async (tab, sessionId) => {
    if (currentTabRef.current !== tab) return;
    updateUrl(tab, sessionId);
    await loadSessions(tab, null, false);
    await loadSessionDetail(sessionId);
  }, []);

  async function openEntity(entityType, entityId) {
    try {
      setProfileLoading(true);
      setProfileError("");
      const res = await adminGetJson(`/api/admin/intelligence/entity-profile?entityType=${encodeURIComponent(entityType)}&entityId=${encodeURIComponent(entityId)}`);
      setProfile(res.profile);
    } catch (err) {
      setProfileError(err.message || String(err));
    } finally {
      setProfileLoading(false);
    }
  }

  const contentProps = {
    sessionId: currentSessionId,
    session: sessionBundle?.session || null,
    messages: sessionBundle?.messages || [],
    schema,
    schemaOptions,
    sessionLoading,
    onSessionStarted,
    onSessionResolved,
    onEntityClick: openEntity,
    onNewSession: handleNew,
  };

  function renderTab() {
    // Key does NOT include currentSessionId. The tab component persists across
    // session changes within the same tab. It only remounts on tab switch (currentTab
    // change) or explicit reset (resetNonce increment). This prevents the component
    // from being destroyed every time a new session starts or the user clicks the sidebar.
    const key = `${currentTab}:${resetNonce}`;
    if (currentTab === "tab1") return <AdminTalkToDataTab key={key} {...contentProps} />;
    if (currentTab === "tab2") return <AdminLiveBidIntelligenceTab key={key} {...contentProps} />;
    if (currentTab === "tab3") return <AdminContentIdeasTab key={key} {...contentProps} />;
    return <AdminInvestigatorTab key={key} {...contentProps} />;
  }

  const sidebarNode = (
    <AdminSessionSidebar
      currentTab={currentTab}
      sessions={sessions}
      activeSessionId={currentSessionId}
      loading={sessionsLoading}
      hasMore={hasMoreSessions}
      onLoadMore={() => loadSessions(currentTab, sessionCursor, true)}
      onSelect={handleSessionSelect}
      onDelete={handleDelete}
      onNew={handleNew}
    />
  );

  return (
    <Layout>
      <div style={pageStyle}>
        <AdminLoadingStyles />

        <div style={{ ...cardStyle, marginBottom: 8, padding: "8px 14px", flexShrink: 0 }}>
          <div style={{ display: "flex", justifyContent: "space-between", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              {isMobile ? <button style={buttonStyle} onClick={() => setSidebarOpen(true)}>Menu</button> : null}
              <div>
                <Link to="/admin" style={{ color: "#64748b", textDecoration: "none", fontSize: 12 }}>{"<-"} Admin Panel</Link>
                <span style={{ fontSize: 18, fontWeight: 900, marginLeft: 12 }}>Admin Intelligence</span>
              </div>
            </div>
            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              {Object.keys(TAB_META).map((id) => <TabButton key={id} id={id} currentTab={currentTab} onClick={handleTabChange} />)}
            </div>
          </div>
        </div>

        <div style={{ display: "flex", flex: 1, minHeight: 0, border: "1px solid #e5e7eb", borderRadius: 18, overflow: "hidden", background: "#fff" }}>
          {!isMobile ? sidebarNode : null}
          <div style={{ flex: 1, minWidth: 0, background: "#f8fafc", padding: 8, overflow: "hidden", display: "flex", flexDirection: "column" }}>{renderTab()}</div>
        </div>

        {isMobile && sidebarOpen ? (
          <div style={{ position: "fixed", inset: 0, background: "rgba(15, 23, 42, 0.28)", zIndex: 1050, display: "flex" }} onClick={() => setSidebarOpen(false)}>
            <div style={{ width: 280, maxWidth: "86vw", height: "100%", background: "#fff" }} onClick={(e) => e.stopPropagation()}>
              {sidebarNode}
            </div>
          </div>
        ) : null}

        {(globalError || schemaError) ? <div style={{ ...cardStyle, color: "#b91c1c", fontWeight: 700, marginTop: 16 }}>Error: {globalError || schemaError}</div> : null}
        
        <EntityDrawer profile={profile} loading={profileLoading} error={profileError} onClose={() => { setProfile(null); setProfileError(""); }} onEntityClick={openEntity} />
      </div>
    </Layout>
  );
}



